import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ListChecks, Plus, Filter, Archive, X, UserPlus } from "lucide-react";
import { CrmTabs } from "@/components/layout/CrmTabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Lists() {
  const { isSuper, roles } = useAuth();
  const canCreate = isSuper || ["account_admin", "group_admin", "recruiting_manager", "internal_recruiter"].some((r) => roles.has(r));
  const [items, setItems] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/lists"); setItems(data.items); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <PageLoader />;

  const archive = async (l) => { try { await api.patch(`/lists/${l.id}`, { archived: true }); load(); toast.success("Archived"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <CrmTabs />
      <PageHeader title="Lists" description="Group People into Manual or Smart lists. Lists never bypass record permissions."
        actions={canCreate ? <Button className="bg-brand hover:bg-brand-strong" data-testid="create-list-button" onClick={() => setOpenCreate(true)}><Plus className="w-4 h-4 mr-1.5" /> New List</Button> : null} />
      {items.length ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lists-grid">
          {items.map((l) => (
            <div key={l.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid={`list-card-${l.id}`} onClick={() => setOpen(l)}>
              <div className="flex items-center justify-between mb-1">
                <span className="flex items-center gap-1.5 font-display font-semibold text-slate-900">{l.list_type === "smart" ? <Filter className="w-4 h-4 text-brand" /> : <ListChecks className="w-4 h-4 text-brand" />}{l.name}</span>
                <StatusBadge status={l.list_type === "smart" ? "pending" : "active"} label={l.list_type} />
              </div>
              <p className="text-xs text-slate-400">{l.description || "—"}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-slate-500">{l.list_type === "manual" ? `${l.member_count} members` : "Dynamic"} · {l.visibility}</span>
                <button onClick={(e) => { e.stopPropagation(); archive(l); }} className="text-slate-300 hover:text-rose-600" data-testid={`list-archive-${l.id}`}><Archive className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState icon={ListChecks} title="No lists yet" description="Create your first manual or smart list." />}

      {openCreate && <CreateListDialog onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
      {open && <ListDrawer list={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function CreateListDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", description: "", list_type: "manual", visibility: "private", filter: {}, organization_id: localStorage.getItem("tr_district") || "" });
  const [districts, setDistricts] = useState([]);
  useEffect(() => { api.get("/agency-districts").then((r) => setDistricts(r.data.items || [])).catch(() => {}); }, []);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return;
    setSaving(true);
    try { await api.post("/lists", { ...f, organization_id: f.visibility === "organization" ? (f.organization_id || null) : null }); toast.success("List created"); onCreated(); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="create-list-dialog">
        <DialogHeader><DialogTitle>New List</DialogTitle><DialogDescription>Manual lists you curate; Smart lists auto-populate from filters.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="list-name" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Type</Label>
              <Select value={f.list_type} onValueChange={(v) => setF({ ...f, list_type: v })}><SelectTrigger className="mt-1" data-testid="list-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="manual">Manual</SelectItem><SelectItem value="smart">Smart</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs font-semibold text-slate-600">Visibility</Label>
              <Select value={f.visibility} onValueChange={(v) => setF({ ...f, visibility: v })}><SelectTrigger className="mt-1" data-testid="list-visibility"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="private">Private</SelectItem><SelectItem value="organization">Organization</SelectItem><SelectItem value="account">Client Account</SelectItem></SelectContent></Select></div>
          </div>
          {f.visibility === "organization" && districts.length > 0 && (
            <div><Label className="text-xs font-semibold text-slate-600">Shared with district</Label>
              <Select value={f.organization_id || ""} onValueChange={(v) => setF({ ...f, organization_id: v })}><SelectTrigger className="mt-1" data-testid="list-district"><SelectValue placeholder="Choose a district" /></SelectTrigger>
                <SelectContent className="bg-white">{districts.map((d) => <SelectItem key={d.org_id} value={d.org_id}>{d.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          {f.list_type === "smart" && (
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-600">Smart filter</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Classification (e.g. candidate)" onChange={(e) => setF({ ...f, filter: { ...f.filter, classification: e.target.value } })} data-testid="smart-classification" />
                <Input placeholder="Candidate type (e.g. producer)" onChange={(e) => setF({ ...f, filter: { ...f.filter, candidate_type: e.target.value } })} />
                <Input placeholder="Status (e.g. active)" onChange={(e) => setF({ ...f, filter: { ...f.filter, status: e.target.value } })} />
                <Input placeholder="Source" onChange={(e) => setF({ ...f, filter: { ...f.filter, source: e.target.value } })} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="list-submit" className="bg-brand hover:bg-brand-strong">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListDrawer({ list, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState(""); const [results, setResults] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get(`/lists/${list.id}/members`); setData(data); }, [list.id]);
  useEffect(() => { load(); }, [load]);
  const search = async (v) => { setQ(v); if (v.length < 2) return setResults([]); const { data } = await api.get("/persons", { params: { search: v, page_size: 8 } }); setResults(data.persons); };
  const add = async (pid) => { await api.post(`/lists/${list.id}/members`, { person_ids: [pid] }); setQ(""); setResults([]); load(); };
  const remove = async (pid) => { await api.delete(`/lists/${list.id}/members/${pid}`); load(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto tr-scroll" data-testid="list-drawer">
        <DialogHeader><DialogTitle>{list.name}</DialogTitle>
          <DialogDescription>{list.list_type === "smart" ? "Smart list — members are filter-driven (read-only)." : "Manual list — add or remove People."}</DialogDescription></DialogHeader>
        {list.list_type === "manual" && (
          <div className="relative mb-3">
            <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search People to add…" data-testid="list-add-search" />
            {results.length > 0 && (
              <div className="absolute z-10 w-full bg-white border border-slate-200 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                {results.map((p) => <button key={p.id} onClick={() => add(p.id)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center gap-2" data-testid={`list-add-${p.id}`}><UserPlus className="w-3.5 h-3.5 text-brand" /> {p.first_name} {p.last_name}</button>)}
              </div>
            )}
          </div>
        )}
        {!data ? <PageLoader /> : (
          <div className="space-y-1.5" data-testid="list-members">
            {data.members.length ? data.members.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`member-${m.id}`}>
                <span className="text-sm text-slate-700">{m.first_name} {m.last_name} <span className="text-slate-400 text-xs">{m.email || ""} {m.state || ""}</span></span>
                {list.list_type === "manual" && <button onClick={() => remove(m.id)} className="text-slate-300 hover:text-rose-600" data-testid={`member-remove-${m.id}`}><X className="w-4 h-4" /></button>}
              </div>
            )) : <p className="text-sm text-slate-400">No members.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
